<!--

	A responsive swipeable list powered by https://swiperjs.com/.

	We could not get the previous Swiper's builtin virtual slides feature to work, so we rolled our own.

-->
<template>
	<conditional :condition="!isEmpty" :message-otherwise="messageIfEmpty" :apply-transition="useTransitionOnEmptying" class="mia-SwiperList" :class="{ 'mia-SwiperList--vertical': isVertical }">
		<swiper-container ref="swiper" v-bind="swiperOptions" init="false" @swiperinit="onReady" @swiperslidechange="onSlideChange">
			<swiper-slide v-for="(itemRow, index) in virtualItems" :key="listItemIndex(index)" class="mia-SwiperList-item">
				<list-item-loader
					v-for="(item, row) in itemRow" :key="row" :from="item" :item-props="itemProps" :conversions="conversions"
					:component="options.component" :index="listItemIndex(index)"
				/>
			</swiper-slide>
		</swiper-container>
		<template #replacement>
			<slot name="empty" />
		</template>
	</conditional>
</template>
<script lang="ts">
import cloneDeep from "lodash-es/cloneDeep";
import { Component, Prop, Watch, toNative } from "vue-facing-decorator";
import { register, type SwiperContainer } from "swiper/element";
import Conditional from "./../../ConditionalReplacement.vue";
import { getMaxSlidesPerView } from "./Items/Config";
import ListItemLoader from "./ListItemLoader.vue";
import { VueListBase } from "./VueListBase";
import type { ISwiperListOptions } from "./VueListOptions";
import type { Swiper } from "swiper";
import type { SwiperOptions } from "swiper/types";
import { Navigation } from "swiper/modules";
import NavigationCss from "swiper/element/css/navigation?raw";

register();

const virtualPages = 2;

@Component({
	components: {
		Conditional,
		ListItemLoader,
	},
	emits: ["swiper-ready"],
})
export class SwiperList<S extends string | number, T> extends VueListBase<S, T, ISwiperListOptions> {
	@Prop({ required: false, type: Number, default: 0 }) protected leftmost!: number;

	mounted() {
		// Because the swiper is not initialized until the first time the list is rendered,
		// we need to watch for changes in the `isEmpty` and the `isResetting` property to
		// initialize the swiper when the list is no longer empty and not resetting.
		this.$watch(() => [this.isEmpty, this.isResetting], ([isEmpty, isResetting]) => {
			if (!isEmpty && !isResetting) {
				this.$nextTick(() => {
					this.initializeSwiper();
				});
			}
		}, { immediate: true });
	}

	initializeSwiper() {
		// $refs can be unreliable and return undefined in rare cases
		// Currently works in 99.9% of cases
		// Investigation findings:
		// - Issue likely caused by ConditionalReplacement component wrapping swiper container
		// - When `this.$refs.swiper` is undefined, logs show `this.$el` as `<!---->`
		// - This indicates conditional rendering prevents proper swiper container mounting
		const swiperContainer = this.getSwiperContainer();

		if (!swiperContainer) {
			throw new Error("Swiper element should be present in mounted.");
		}

		const swiperParams = {
			modules: [Navigation],
			injectStyles: [
				`
					.swiper-button-disabled {
						display: var(--swiper-button-disabled-display, flex);
					}

					.swiper-button-next, 
					.swiper-button-prev {
						top: var(--swiper-navigation-top-offset, 50%);
					}
				`,
				NavigationCss,
			],
		};

		Object.assign(swiperContainer, swiperParams);

		swiperContainer.initialize();
	}

	/**
	 * We pretend to swiper that this subset of itemsAsRows are all the items that exist.
	 * This way we only keep a small number of items in the DOM, namely virtualPages many pages in each direction.
	 */
	virtualItems = [] as S[][];

	/**
	 * The offset between itemsAsRows and virtualItems, i.e., itemsAsRows[i + virtualOffset] == virtualItems[i].
	 */
	protected virtualOffset = 0;

	private getSwiperContainer(): SwiperContainer | undefined {
		return this.$refs.swiper as SwiperContainer | undefined;
	}

	private getSwiper(): Swiper | undefined {
		return this.getSwiperContainer()?.swiper;
	}

	/**
	 * Move the swiper such that list item `offset` (zero based index) is in the
	 * leftmost visible position. Equivalently, move such that the slides
	 * `offset/slidesPerColumn` is in the leftmost visible position.
	 */
	@Watch("leftmost", { immediate: true })
	public async goto(offset: number): Promise<void> {
		await this.preload(offset);

		this.virtualOffset = Math.floor(offset / this.slidesPerColumn);

		const swiper = this.getSwiper();

		if (swiper == null) {
			// If the swiper has not initialized yet, we cannot do much. We will wait
			// for it which will call us again from onReady().
			return;
		}

		if (swiper.destroyed) {
			return;
		}

		// The virtualOffset has been set such that the very first virtual slide is
		// the slide corresponding to offset. We now go to that very first slide.
		// The virtual slides will be mangled a bit by refreshFrom*() but the
		// currently "active" slide will be kept on the visible page. I.e., we will
		// effectively go to the offset slide.
		swiper.slideTo(0, 0, false);

		this.virtualItems = [];
		this.refreshFromBackend();
		this.refreshFromFrontend();
	}

	get swiperOptions(): SwiperOptions {
		const options = cloneDeep(this.backendOptions) as any;
		// Remove the options from ISwiperListOptions that this component handles
		// but that the underlying swiper does not understand.
		delete options.slidesPerColumn;
		delete options.cssClass;
		delete options.displayAdjacentItems;
		delete options.useTransitionOnEmptying;

		return options;
	}

	get slidesPerColumn(): number {
		return this.backendOptions.slidesPerColumn;
	}

	get isVertical(): boolean {
		return this.backendOptions.direction === "vertical";
	}

	get useTransitionOnEmptying(): boolean {
		return this.backendOptions.useTransitionOnEmptying ?? true;
	}

	async onReady() {
		// When the swiper signals that it is ready, it still takes a Vue tick for
		// the swiper backend to be set on the swiper element (for an unknown
		// reason.) So we wait for a tick and then shift the list to the correct slide.
		await this.$nextTick();
		console.assert(this.getSwiper() != null);
		this.$emit("swiper-ready", this.getSwiper());
		// Navigate to the initial `leftmost` item now that the swiper exists. We read
		// `leftmost` directly rather than deriving it from `virtualOffset`: the initial
		// `@Watch("leftmost")` goto is async (it awaits preload) and may not have set
		// `virtualOffset` yet when onReady runs, so the stale `virtualOffset` of 0 would
		// snap the list back to the top and override the intended initial position.
		void this.goto(this.leftmost);
	}

	get itemsAsRows(): S[][] {
		const items = this.items;
		if (items.length === 0) {
			return [];
		}

		const ret: S[][] = [[]];
		for (const item of items) {
			if (ret[ret.length - 1].length >= this.slidesPerColumn) {
				ret.push([]);
			}
			ret[ret.length - 1].push(item);
		}
		return ret;
	}

	@Watch("isResetting")
	protected onReset(isResetting: number): void {
		if (!isResetting) {
			void this.goto(this.leftmost);
		}
	}

	listItemIndex(index: number) {
		return this.virtualOffset + index;
	}

	@Watch("itemsAsRows", { immediate: true })
	protected handler() {
		this.refreshFromBackend();
		this.refreshFromFrontend();
	}

	onSlideChange() {
		const swiperContainer = this.getSwiperContainer();
		const swiper = this.getSwiper();
		if (swiperContainer == null || swiper == null) {
			throw Error("onSlideChange must be called by an existing swiper but why is there none?");
		}

		const firstVisibleItem = ((swiper.activeIndex as number) + this.virtualOffset) * this.slidesPerColumn;
		void this.preload(firstVisibleItem);
		swiperContainer.addEventListener("swiperslidechangetransitionend", () => this.refreshFromFrontend(), { once: true });
	}

	/**
	 * Update virtualItems with elements from itemsAsRows.
	 * Called when data in the backend changes or when the virtualOffset was modified.
	 */
	private refreshFromBackend() {
		const newVirtualItems = [];
		const maxSlidesPerView = getMaxSlidesPerView(this.backendOptions);
		for (let i = 0; i < maxSlidesPerView * (2 * virtualPages + 1); i++) {
			const offset = this.virtualOffset + i;
			if (offset >= 0 && offset < this.itemsAsRows.length) {
				newVirtualItems.push(this.itemsAsRows[offset]);
			}
			else {
				break;
			}
		}
		this.virtualItems = newVirtualItems;
	}

	/**
	 * Update virtualItems when the swiper might run out of items to display soon.
	 * Called after each swipe.
	 */
	private refreshFromFrontend() {
		const swiper = this.getSwiper();
		if (swiper == null) {
			// The swiper has not initialized yet. When it initializes, it will call
			// refreshFromFrontend() again through goto() and so override anything
			// we'd do here anyway.
			return;
		}
		let activeColumn = swiper.activeIndex;
		const maxSlidesPerView = getMaxSlidesPerView(this.options.backendOptions as ISwiperListOptions);
		// Whenever changing to the previous or the following page would get us to the edge of the virtual slides,
		// we reset the virtual slides such that we are again in the center of them.
		// (We never want to get to the edge because it makes the previous/next buttons flicker.)
		if (activeColumn - maxSlidesPerView <= 0
			|| activeColumn + maxSlidesPerView + maxSlidesPerView >= this.virtualItems.length) {
			// We now move this.virtualOffset so that the activeColumn is not at boundary anymore.
			while (true) {
				// Determine which side of the center we are on with activeColumn; when this is zero, then we are in the center
				// of the virtualItems already and we cannot improve the situation anymore.
				const activeColumnDelta = Math.sign(maxSlidesPerView * virtualPages - activeColumn);
				if (activeColumnDelta === 0) {
					break;
				}
				// We move the virtualOffset into the opposite direction to move the activeColumnDelta towards zero.
				const virtualOffsetDelta = -activeColumnDelta;
				if (this.virtualOffset + virtualOffsetDelta < 0) {
					// We cannot go before the first page. Abort if the virtualOffset is already minimal.
					break;
				}
				if (virtualOffsetDelta > 0
					&& this.virtualOffset + virtualOffsetDelta + maxSlidesPerView * (2 * virtualPages + 1) > this.itemsAsRows.length) {
					// There is no more data so we cannot move the virtualOffset any further, symmetric to the preceding case.
					// We stop here but it's important that refreshFromBackend() calls us again, so we move further once new
					// data comes in.
					break;
				}
				// move virtualOffset and activeColumn one step in the right direction and try again.
				activeColumn += activeColumnDelta;
				this.virtualOffset += virtualOffsetDelta;
			}
		}

		if (activeColumn !== swiper.activeIndex) {
			this.refreshFromBackend();
			// Note that there can be a slight flicker here (depending on the size of involved images.)
			// There is not much we can do about it as Vue's DOM changes are picked up with the wrong offset originally.
			// Unfortunately Swiper's native virtual slides did not work sufficiently well in late 2018 to use them.
			this.$nextTick(() => {
				// Ensure the swiper has picked up the freshly rendered virtual slides before
				// moving to one of them. Without this, sliding to a non-zero index right after
				// the slide set changes is silently ignored (seen on vertical lists), leaving
				// the list stuck at the top.
				swiper.update();
				swiper.slideTo(activeColumn, 0, false);
			});
		}
	}
}

export default toNative(SwiperList);
</script>
<style lang="scss">
	.mia-SwiperList {
		// Since we want to move the prev/next button outside of the list container we need to make sure that
		// the positioned ancestor is at the same time an ancestor of the element with `overflow: hidden`,
		// see https://css-tricks.com/popping-hidden-overflow/.
		// This is admittedly an evil hack but there is no other easy way. If this breaks things, we have to produce
		// our own buttons (outside of the swiper) and wire them to the swiper which is substantially more work.
		swiper-container::part(container) {
			position: static !important;
		}

		// Move the buttons outside of their list container so they do not overlap too much with the content here.
		swiper-container::part(button-prev) {
			left: -#{theme.$mia-padding-main-column-horizontal};
		}

		// Move the buttons outside of their list container so they do not overlap too much with the content here.
		swiper-container::part(button-next) {
			right: -#{theme.$mia-padding-main-column-horizontal};
		}

		// Move the buttons outside of their list container so they do not overlap too much with the content here.
		@media(max-width: theme.$grid-float-breakpoint) {
			swiper-container::part(button-prev) {
				left: -#{theme.$mia-padding-main-column-horizontal-xs};
			}

			swiper-container::part(button-next) {
				right: -#{theme.$mia-padding-main-column-horizontal-xs};
			}
		}

		swiper-container::part(button-prev),
		swiper-container::part(button-next) {
			background-size: 100% auto;
			background-repeat: no-repeat;
			height: theme.$mia-swiper-nav-icon-height;
		}

		swiper-container::part(button-prev) {
			background-image: url("@theme/swiper-button-prev.svg");
			color: transparent;
		}

		swiper-container::part(button-next) {
			background-image: url("@theme/swiper-button-next.svg");
			color: transparent;
		}

		&.mia-SwiperList--vertical {
			position: relative;

			swiper-container {
				position: static !important;
			}

				swiper-container::part(button-prev),
				swiper-container::part(button-next) {
					left: 50%;
					right: auto;
					width: theme.$mia-swiper-nav-icon-width;
					margin: 0;
					background-size: contain;
					background-position: center;
				}

				swiper-container::part(button-prev) {
					top: 0;
					bottom: auto;
					transform: translate(-50%, calc((#{theme.$mia-swiper-nav-icon-width} - #{theme.$mia-swiper-nav-icon-height}) / 2)) rotate(90deg);
				}

				swiper-container::part(button-next) {
					top: auto;
					bottom: 0;
					transform: translate(-50%, calc((#{theme.$mia-swiper-nav-icon-height} - #{theme.$mia-swiper-nav-icon-width}) / 2)) rotate(90deg);
				}
		}
	}
</style>
